/**
 * Activity log modal.
 *
 * Centered shared `Modal` from `@benchmark/ui` (size="lg") so the
 * activity log carries the same rounded chrome + custom close button as
 * every other modal in the app. Lists every care-time entry for the
 * visible month for a patient. Replaces the right-anchored
 * `ActivityLogDrawer`. Soft-deleted entries are hidden by default; the
 * "Show removed entries" toggle reveals them struck-through (D3).
 *
 * Inline edit per row (D4): pencil reveals on hover, click swaps the
 * row to `CareTimeEntryRowEditor`, ✓/✗ commit/cancel without stacking
 * a second modal.
 *
 * @agentic connections
 */

import { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Center,
  Divider,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { Modal } from "@benchmark/ui/components/atomic";
import {
  IconChevronLeft,
  IconChevronRight,
  IconClockHour4,
} from "@tabler/icons-react";
import { CareTimeEntryRow } from "./CareTimeEntryRow";
import { useCareTimeLog } from "@/hooks/domain/useCareTimeLog";
import { useCareTimeSummary } from "@/hooks/domain/useCareTimeSummary";

/** Props for `ActivityLogModal`. @agentic connections */
export type ActivityLogModalProps = {
  opened: boolean;
  onClose: () => void;
  patientId: string;
  patientDisplay: string;
};

// The month picker stores months as Dates anchored at NOON UTC on the
// 1st of each month. Anchoring at noon UTC keeps the calendar day on
// the 1st across every local timezone (the prior 00:00 UTC anchor
// shifted to the previous day — and thus the previous month — for any
// behind-UTC locale, producing "April 2026" titles in May for EDT).
const monthLabel = (date: Date): string =>
  date.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

const monthKey = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

// Earliest month for which care-time data exists. The navigator pages
// back to this month but no further. `monthIndex` is 0-based, so 9 is
// October. October 2025.
const EARLIEST_DATA_YEAR = 2025;
const EARLIEST_DATA_MONTH_INDEX = 9;

const buildRecentMonths = (): Date[] => {
  const now = new Date();
  // Derive the "current month" from local-time components so the
  // user's wall-clock month — not UTC's idea of it — drives the
  // navigator. The resulting Date is still anchored at noon UTC for
  // stable formatting + month-bounds computation downstream.
  const baseYear = now.getFullYear();
  const baseMonth = now.getMonth();
  // Span every month from the current month back to the earliest data
  // month, inclusive. Clamped to at least 1 so a clock set before the
  // earliest month still yields a usable (current-month-only) navigator.
  const monthsSinceEarliest =
    (baseYear - EARLIEST_DATA_YEAR) * 12 +
    (baseMonth - EARLIEST_DATA_MONTH_INDEX);
  const count = Math.max(monthsSinceEarliest + 1, 1);
  return Array.from(
    { length: count },
    (_, i) => new Date(Date.UTC(baseYear, baseMonth - i, 1, 12, 0, 0, 0)),
  );
};

/**
 * Activity log modal for care-time entries.
 * @agentic connections
 */
export const ActivityLogModal = ({
  opened,
  onClose,
  patientId,
  patientDisplay,
}: ActivityLogModalProps) => {
  const months = useMemo(() => buildRecentMonths(), []);
  const [selectedMonth, setSelectedMonth] = useState<Date>(months[0]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [editingDirty, setEditingDirty] = useState(false);

  const { entries, loading: logLoading } = useCareTimeLog(patientId, {
    month: selectedMonth,
  });
  const { summary, loading: summaryLoading } = useCareTimeSummary(patientId, {
    month: selectedMonth,
  });

  const visibleEntries = showRemoved
    ? entries
    : entries.filter((e) => e.status !== "entered-in-error");

  const goPrev = () => {
    const idx = months.findIndex(
      (m) => monthKey(m) === monthKey(selectedMonth),
    );
    if (idx >= 0 && idx < months.length - 1) {
      setSelectedMonth(months[idx + 1]);
    }
  };
  const goNext = () => {
    const idx = months.findIndex(
      (m) => monthKey(m) === monthKey(selectedMonth),
    );
    if (idx > 0) {
      setSelectedMonth(months[idx - 1]);
    }
  };

  const monthOptions = months.map((m) => ({
    value: monthKey(m),
    label: monthLabel(m),
  }));

  const onMonthSelect = (value: string | null) => {
    if (!value) return;
    const next = months.find((m) => monthKey(m) === value);
    if (next) setSelectedMonth(next);
  };

  const requestEdit = (id: string) => {
    if (editingId && editingDirty && editingId !== id) {
      const proceed = window.confirm(
        "Discard your unsaved changes to switch rows?",
      );
      if (!proceed) return;
    }
    setEditingDirty(false);
    setEditingId(id);
  };

  const handleClose = () => {
    if (editingDirty) {
      const proceed = window.confirm(
        "Discard your unsaved care-time edit and close?",
      );
      if (!proceed) return;
    }
    setEditingDirty(false);
    setEditingId(undefined);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconClockHour4 size={18} />
          <Text fw={600}>Activity Log — {monthLabel(selectedMonth)}</Text>
        </Group>
      }
      aria-label={`Activity log for ${patientDisplay}`}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ActionIcon
              variant="subtle"
              onClick={goPrev}
              aria-label="Previous month"
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
            <Select
              value={monthKey(selectedMonth)}
              data={monthOptions}
              onChange={onMonthSelect}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              size="xs"
              style={{ minWidth: 200 }}
            />
            <ActionIcon
              variant="subtle"
              onClick={goNext}
              aria-label="Next month"
              disabled={monthKey(selectedMonth) === monthKey(months[0])}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Group>
          <Switch
            label="Show removed entries"
            size="xs"
            checked={showRemoved}
            onChange={(e) => setShowRemoved(e.currentTarget.checked)}
          />
        </Group>

        <Box
          p="md"
          style={{
            backgroundColor: "var(--mantine-color-purple-0)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Stack gap={2} aria-label="Care time this month" role="group">
            <Group gap="xs" wrap="nowrap" align="center">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Care time this month
              </Text>
              {summary?.hasCoCMCarePlan &&
              summary.billingResult?.packageDisplay ? (
                <Badge size="xs" variant="filled" color="teal">
                  {summary.billingResult.packageDisplay}
                </Badge>
              ) : null}
            </Group>
            <Text size="xl" fw={700}>
              {summaryLoading
                ? "Calculating…"
                : `${
                    summary?.hasCoCMCarePlan
                      ? (summary.billableMinutes ?? 0)
                      : (summary?.totalMinutes ?? 0)
                  } min`}
            </Text>
            {!summaryLoading && summary && !summary.hasCoCMCarePlan ? (
              <Text size="xs" c="dimmed">
                Not billable — patient not enrolled in CoCM
              </Text>
            ) : null}
            {!summaryLoading &&
            summary?.hasCoCMCarePlan &&
            summary.nonBillableMinutes > 0 ? (
              <Text size="xs" c="dimmed">
                {summary.nonBillableMinutes} min non-billable (excluded from
                billing)
              </Text>
            ) : null}
          </Stack>
        </Box>

        <Divider />

        <ScrollArea h={420}>
          {logLoading ? (
            <Center py="lg">
              <Loader size="sm" />
            </Center>
          ) : visibleEntries.length === 0 ? (
            <Center py="lg">
              <Text size="sm" c="dimmed">
                No care time logged this month.
              </Text>
            </Center>
          ) : (
            <Stack gap={0}>
              {visibleEntries.map((entry, index) => (
                <Stack key={entry.id} gap={0}>
                  {index > 0 ? <Divider /> : null}
                  <CareTimeEntryRow
                    observation={entry}
                    patientDisplay={patientDisplay}
                    editing={editingId === entry.id}
                    onRequestEdit={
                      entry.id
                        ? () => requestEdit(entry.id as string)
                        : undefined
                    }
                    onEditDone={() => {
                      setEditingId(undefined);
                      setEditingDirty(false);
                    }}
                    onEditDirtyChange={(d) => setEditingDirty(d)}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </ScrollArea>
      </Stack>
    </Modal>
  );
};
